import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import {
  applyBulkPartsCost,
  autocompletePartsCost,
  listPartsCostBatches,
  listPartsCostRows,
  listPartsCostServices,
  previewBulkPartsCost,
  saveExactPartsCost,
  undoPartsCostBatch,
  type PartsCostRow,
  type PartsCostSuggestion,
} from '@/lib/api/data-cleanup.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { Pagination } from '@/components/common/Pagination.js';
import { Link } from 'react-router-dom';

function yearDefaults() {
  const y = new Date().getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

function CopyTicketNumber({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-mono text-sm font-bold text-primary hover:underline"
      title="Copy ticket number"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        } catch {
          toastError('Could not copy');
        }
      }}
    >
      {value}
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

type Draft = {
  partName: string;
  unitCost: string;
  inventoryItemId: string | null;
};

export function DataCleanupPartsCostsPage() {
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const queryClient = useQueryClient();
  const { requireOverride, modalProps } = useManagerOverride();
  const managerByRef = useRef<string | null>(null);

  const defaults = useMemo(() => yearDefaults(), []);
  const [mode, setMode] = useState<'exact' | 'bulk' | 'history'>('exact');
  const [createdFrom, setCreatedFrom] = useState(defaults.from);
  const [createdTo, setCreatedTo] = useState(defaults.to);
  const [serviceName, setServiceName] = useState('');
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [hasParts, setHasParts] = useState<'yes' | 'no' | 'any'>('no');
  const [hideCompleted, setHideCompleted] = useState(true);
  const [groupByService, setGroupByService] = useState(true);
  const [page, setPage] = useState(1);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [suggestions, setSuggestions] = useState<PartsCostSuggestion[]>([]);
  const [suggestFor, setSuggestFor] = useState<string | null>(null);
  const partInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Mode B
  const [bulkService, setBulkService] = useState('');
  const [bulkMakeId, setBulkMakeId] = useState('');
  const [bulkModelId, setBulkModelId] = useState('');
  const [bulkFrom, setBulkFrom] = useState(defaults.from);
  const [bulkTo, setBulkTo] = useState(defaults.to);
  const [bulkPartName, setBulkPartName] = useState('');
  const [bulkCost, setBulkCost] = useState('');
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<Awaited<ReturnType<typeof previewBulkPartsCost>> | null>(
    null,
  );

  const listQ = useQuery({
    queryKey: [
      'data-cleanup-parts-costs',
      createdFrom,
      createdTo,
      serviceName,
      makeId,
      modelId,
      hasParts,
      hideCompleted,
      groupByService,
      page,
    ],
    queryFn: () =>
      listPartsCostRows({
        createdFrom,
        createdTo,
        serviceName: serviceName || undefined,
        makeId: makeId || undefined,
        modelId: modelId || undefined,
        hasParts,
        hideCompleted,
        groupByService,
        page,
        pageSize: 40,
      }),
    enabled: isTenantAdmin && mode === 'exact',
  });

  const servicesQ = useQuery({
    queryKey: ['data-cleanup-parts-services'],
    queryFn: listPartsCostServices,
    enabled: isTenantAdmin,
  });

  const makesQ = useQuery({
    queryKey: ['device-makes'],
    queryFn: () => listDeviceMakes(),
    enabled: isTenantAdmin,
  });

  const modelsQ = useQuery({
    queryKey: ['device-models', makeId || bulkMakeId],
    queryFn: () => listDeviceModels({ makeId: makeId || bulkMakeId }),
    enabled: isTenantAdmin && Boolean(makeId || bulkMakeId),
  });

  const batchesQ = useQuery({
    queryKey: ['data-cleanup-parts-batches'],
    queryFn: listPartsCostBatches,
    enabled: isTenantAdmin && mode === 'history',
  });

  const rows = listQ.data?.data ?? [];
  const meta = listQ.data?.meta;

  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        if (!next[row.ticketRepairId]) {
          next[row.ticketRepairId] = { partName: '', unitCost: '', inventoryItemId: null };
        }
      }
      return next;
    });
  }, [rows]);

  const ensureManager = useCallback(async () => {
    if (managerByRef.current) return managerByRef.current;
    const result = await requireOverride({
      action: 'data_cleanup',
      title: 'Confirm parts cost backfill',
      description: 'Confirm with a manager PIN to record historical parts costs.',
      requiredRole: 'tenant_admin',
      context: { reason: 'parts_cost_backfill' },
    });
    if (!result.approved) throw new Error('Manager override required');
    managerByRef.current = result.authorizedBy.id;
    return result.authorizedBy.id;
  }, [requireOverride]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['data-cleanup-parts-costs'] });
    void queryClient.invalidateQueries({ queryKey: ['data-cleanup-parts-batches'] });
  };

  const saveMut = useMutation({
    mutationFn: async (row: PartsCostRow) => {
      const draft = drafts[row.ticketRepairId] ?? { partName: '', unitCost: '', inventoryItemId: null };
      const unitCost = Number(draft.unitCost);
      if (!Number.isFinite(unitCost) || unitCost < 0) {
        throw new Error('Enter a valid cost');
      }
      const authorizedBy = await ensureManager();
      return saveExactPartsCost({
        ticketRepairId: row.ticketRepairId,
        parts: [
          {
            partName: draft.partName.trim() || null,
            unitCost,
            inventoryItemId: draft.inventoryItemId,
          },
        ],
        overwriteExisting: false,
        managerOverrideAuthorizedBy: authorizedBy,
      });
    },
    onSuccess: (_data, row) => {
      toastSuccess('Cost saved');
      setDrafts((prev) => ({
        ...prev,
        [row.ticketRepairId]: { partName: '', unitCost: '', inventoryItemId: null },
      }));
      invalidate();
      const idx = rows.findIndex((r) => r.ticketRepairId === row.ticketRepairId);
      const next = rows[idx + 1];
      if (next) {
        window.requestAnimationFrame(() => {
          partInputRefs.current[next.ticketRepairId]?.focus();
        });
      }
    },
    onError: (err) => toastError(parseApiError(err)),
  });

  const bulkPreviewMut = useMutation({
    mutationFn: () => {
      const unitCost = Number(bulkCost);
      if (!bulkService.trim()) throw new Error('Select a service');
      if (!Number.isFinite(unitCost) || unitCost < 0) throw new Error('Enter a valid unit cost');
      return previewBulkPartsCost({
        serviceName: bulkService,
        makeId: bulkMakeId || null,
        modelId: bulkModelId || null,
        createdFrom: bulkFrom,
        createdTo: bulkTo,
        unitCost,
        overwriteExisting: bulkOverwrite,
      });
    },
    onSuccess: (data) => setBulkPreview(data),
    onError: (err) => toastError(parseApiError(err)),
  });

  const bulkApplyMut = useMutation({
    mutationFn: async () => {
      const unitCost = Number(bulkCost);
      const authorizedBy = await ensureManager();
      return applyBulkPartsCost({
        serviceName: bulkService,
        makeId: bulkMakeId || null,
        modelId: bulkModelId || null,
        createdFrom: bulkFrom,
        createdTo: bulkTo,
        partName: bulkPartName.trim() || null,
        unitCost,
        overwriteExisting: bulkOverwrite,
        managerOverrideAuthorizedBy: authorizedBy,
      });
    },
    onSuccess: (data) => {
      toastSuccess(`Applied costs to ${data.repairCount ?? data.ticketCount} repairs`);
      setBulkPreview(null);
      invalidate();
    },
    onError: (err) => toastError(parseApiError(err)),
  });

  const undoMut = useMutation({
    mutationFn: async (batchId: string) => {
      const authorizedBy = await ensureManager();
      return undoPartsCostBatch(batchId, authorizedBy);
    },
    onSuccess: () => {
      toastSuccess('Batch undone');
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['data-cleanup-parts-batches'] });
    },
    onError: (err) => toastError(parseApiError(err)),
  });

  const loadSuggestions = useCallback(async (repairId: string, q: string) => {
    if (q.trim().length < 1) {
      setSuggestions([]);
      setSuggestFor(null);
      return;
    }
    try {
      const list = await autocompletePartsCost(q);
      setSuggestions(list);
      setSuggestFor(repairId);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const copyFromAbove = (index: number) => {
    if (index <= 0) return;
    const prev = rows[index - 1];
    const cur = rows[index];
    if (!prev || !cur) return;
    const prevDraft = drafts[prev.ticketRepairId];
    const fromExisting = prev.existingParts[0];
    setDrafts((d) => ({
      ...d,
      [cur.ticketRepairId]: {
        partName: prevDraft?.partName || fromExisting?.partName || '',
        unitCost: prevDraft?.unitCost || fromExisting?.unitCost || '',
        inventoryItemId: prevDraft?.inventoryItemId ?? null,
      },
    }));
  };

  const onRowKeyDown = (e: KeyboardEvent, row: PartsCostRow, index: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveMut.mutate(row);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      copyFromAbove(index);
    }
  };

  if (!isTenantAdmin) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950">
        Tenant admin access required for data cleanup.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">
            <Link to="/settings/data-cleanup/balances" className="text-primary hover:underline">
              Data Cleanup
            </Link>
            {' · '}
            Parts costs
          </p>
          <h1 className="text-xl font-bold text-slate-950">Historical parts cost backfill</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            RepairDesk imports have revenue but no part costs. Enter costs here without touching
            inventory stock. Costs land on the ticket&apos;s date for P&amp;L.
          </p>
        </div>
        {meta ? (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
            <p className="font-semibold text-slate-900">
              {meta.completedTickets.toLocaleString()} of {meta.totalTickets.toLocaleString()}
            </p>
            <p className="text-slate-500">{formatCurrency(meta.costsRecorded)} in costs recorded</p>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['exact', 'Per-ticket entry'],
            ['bulk', 'Bulk by service'],
            ['history', 'Undo history'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-sm font-semibold',
              mode === id
                ? 'bg-primary text-white'
                : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'exact' ? (
        <>
          <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-xs font-medium text-slate-600">
              From
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={createdFrom}
                onChange={(e) => {
                  setCreatedFrom(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              To
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={createdTo}
                onChange={(e) => {
                  setCreatedTo(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Service contains
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={serviceName}
                onChange={(e) => {
                  setServiceName(e.target.value);
                  setPage(1);
                }}
                placeholder="Screen Replacement"
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Has parts
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={hasParts}
                onChange={(e) => {
                  setHasParts(e.target.value as 'yes' | 'no' | 'any');
                  setPage(1);
                }}
              >
                <option value="no">No parts yet</option>
                <option value="yes">Has parts</option>
                <option value="any">Any</option>
              </select>
            </label>
            <label className="text-xs font-medium text-slate-600">
              Device make
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={makeId}
                onChange={(e) => {
                  setMakeId(e.target.value);
                  setModelId('');
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {(makesQ.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-600">
              Device model
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={modelId}
                disabled={!makeId}
                onChange={(e) => {
                  setModelId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {(modelsQ.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-end gap-2 pb-1 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={groupByService}
                onChange={(e) => setGroupByService(e.target.checked)}
              />
              Group by service
            </label>
            <label className="flex items-end gap-2 pb-1 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => {
                  setHideCompleted(e.target.checked);
                  setPage(1);
                }}
              />
              Hide completed
            </label>
          </div>

          <p className="text-xs text-slate-500">
            Shortcuts: Enter saves &amp; advances · Tab between fields · Ctrl+D copies part/cost from
            row above · blank part name → MANUAL_ENTRY_PART
          </p>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Ticket #</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Part</th>
                  <th className="px-3 py-2">Cost</th>
                  <th className="px-3 py-2 w-24" />
                </tr>
              </thead>
              <tbody>
                {listQ.isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-slate-500">
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-slate-500">
                      No matching repairs. Adjust filters or date range.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const draft = drafts[row.ticketRepairId] ?? {
                      partName: '',
                      unitCost: '',
                      inventoryItemId: null,
                    };
                    return (
                      <tr
                        key={row.ticketRepairId}
                        className={cn(
                          'border-b border-slate-100',
                          row.hasCost && 'bg-emerald-50/40',
                        )}
                      >
                        <td className="px-3 py-2 align-top">
                          <CopyTicketNumber value={row.ticketNumber} />
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-3 py-2 align-top">{row.deviceLabel}</td>
                        <td className="px-3 py-2 align-top">{row.serviceName}</td>
                        <td className="relative px-3 py-2 align-top">
                          {row.existingParts.length > 0 ? (
                            <div className="mb-1 space-y-0.5">
                              {row.existingParts.map((p) => (
                                <div key={p.id} className="text-xs text-slate-600">
                                  {p.partName} · {formatCurrency(p.totalCost)}{' '}
                                  <span className="text-slate-400">
                                    ({p.costSource === 'approximate' ? 'approx' : 'exact'})
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <input
                            ref={(el) => {
                              partInputRefs.current[row.ticketRepairId] = el;
                            }}
                            className="w-full min-w-[10rem] rounded border border-slate-300 px-2 py-1"
                            value={draft.partName}
                            placeholder="Part name"
                            onChange={(e) => {
                              const v = e.target.value;
                              setDrafts((d) => ({
                                ...d,
                                [row.ticketRepairId]: {
                                  ...draft,
                                  partName: v,
                                  inventoryItemId: null,
                                },
                              }));
                              void loadSuggestions(row.ticketRepairId, v);
                            }}
                            onKeyDown={(e) => onRowKeyDown(e, row, index)}
                          />
                          {suggestFor === row.ticketRepairId && suggestions.length > 0 ? (
                            <ul className="absolute z-20 mt-1 max-h-48 w-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                              {suggestions.map((s) => (
                                <li key={`${s.source}-${s.partName}`}>
                                  <button
                                    type="button"
                                    className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                                    onClick={() => {
                                      setDrafts((d) => ({
                                        ...d,
                                        [row.ticketRepairId]: {
                                          partName: s.partName,
                                          unitCost: s.unitCost,
                                          inventoryItemId: s.inventoryItemId,
                                        },
                                      }));
                                      setSuggestions([]);
                                      setSuggestFor(null);
                                    }}
                                  >
                                    <span>{s.partName}</span>
                                    <span className="text-xs text-slate-500">
                                      {formatCurrency(s.unitCost)} ·{' '}
                                      {s.source === 'inventory' ? 'inv' : 'prior'}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <input
                            className="w-24 rounded border border-slate-300 px-2 py-1"
                            inputMode="decimal"
                            value={draft.unitCost}
                            placeholder="0.00"
                            onChange={(e) =>
                              setDrafts((d) => ({
                                ...d,
                                [row.ticketRepairId]: { ...draft, unitCost: e.target.value },
                              }))
                            }
                            onKeyDown={(e) => onRowKeyDown(e, row, index)}
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex gap-1">
                            <button
                              type="button"
                              title="Same as above (Ctrl+D)"
                              className="rounded border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50"
                              onClick={() => copyFromAbove(index)}
                              disabled={index === 0}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="rounded bg-primary px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                              disabled={saveMut.isPending}
                              onClick={() => saveMut.mutate(row)}
                            >
                              ✓
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {meta ? (
            <Pagination
              page={meta.page}
              pageSize={meta.pageSize}
              total={meta.total}
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : null}

      {mode === 'bulk' ? (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-600">
            Apply one approximate unit cost to every matching imported repair. Skips repairs that
            already have parts unless overwrite is checked. Marked as approximate in reports.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="text-xs font-medium text-slate-600">
              Service
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={bulkService}
                onChange={(e) => setBulkService(e.target.value)}
              >
                <option value="">Select…</option>
                {(servicesQ.data ?? []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-600">
              Device make (optional)
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={bulkMakeId}
                onChange={(e) => {
                  setBulkMakeId(e.target.value);
                  setBulkModelId('');
                }}
              >
                <option value="">All devices</option>
                {(makesQ.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-600">
              Device model (optional)
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={bulkModelId}
                disabled={!bulkMakeId}
                onChange={(e) => setBulkModelId(e.target.value)}
              >
                <option value="">All models</option>
                {(modelsQ.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-600">
              From
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={bulkFrom}
                onChange={(e) => setBulkFrom(e.target.value)}
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              To
              <input
                type="date"
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={bulkTo}
                onChange={(e) => setBulkTo(e.target.value)}
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Part name
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={bulkPartName}
                onChange={(e) => setBulkPartName(e.target.value)}
                placeholder="OLED Assembly iPhone 14"
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Unit cost
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                inputMode="decimal"
                value={bulkCost}
                onChange={(e) => setBulkCost(e.target.value)}
                placeholder="89.50"
              />
            </label>
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={bulkOverwrite}
                onChange={(e) => setBulkOverwrite(e.target.checked)}
              />
              Overwrite existing parts
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
              disabled={bulkPreviewMut.isPending}
              onClick={() => bulkPreviewMut.mutate()}
            >
              Preview matches
            </button>
            <button
              type="button"
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!bulkPreview?.matchCount || bulkApplyMut.isPending}
              onClick={() => bulkApplyMut.mutate()}
            >
              Apply to all
            </button>
          </div>

          {bulkPreview ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="font-semibold text-slate-900">
                Matches {bulkPreview.matchCount.toLocaleString()} tickets · applies{' '}
                {formatCurrency(bulkPreview.totalCost)} in costs
              </p>
              <ul className="mt-3 max-h-64 overflow-auto text-sm">
                {bulkPreview.tickets.slice(0, 100).map((t) => (
                  <li key={t.ticketRepairId} className="flex justify-between border-b border-slate-200 py-1">
                    <span>
                      <span className="font-mono font-semibold">{t.ticketNumber}</span> · {t.deviceLabel}
                    </span>
                    <span className="text-slate-500">{formatDate(t.createdAt)}</span>
                  </li>
                ))}
                {bulkPreview.tickets.length > 100 ? (
                  <li className="py-1 text-slate-500">…and {bulkPreview.tickets.length - 100} more</li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'history' ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Operation</th>
                <th className="px-3 py-2">Tickets</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {(batchesQ.data ?? []).map((b) => (
                <tr key={b.id} className="border-b border-slate-100">
                  <td className="px-3 py-2">{formatDate(b.performedAt)}</td>
                  <td className="px-3 py-2">
                    {b.operation === 'parts_cost_bulk' ? 'Bulk (approx)' : 'Exact'}
                    {b.reversedAt ? (
                      <span className="ml-2 text-xs text-slate-400">undone</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{b.ticketCount}</td>
                  <td className="px-3 py-2">{b.totalAmount ? formatCurrency(b.totalAmount) : '—'}</td>
                  <td className="px-3 py-2">
                    {!b.reversedAt ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-red-700 hover:underline"
                        disabled={undoMut.isPending}
                        onClick={() => undoMut.mutate(b.id)}
                      >
                        Undo
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!batchesQ.isLoading && !(batchesQ.data ?? []).length ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-slate-500">
                    No parts-cost batches yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
